import React, { useEffect, useMemo, useState } from 'react';
import {
  Clock3,
  CreditCard,
  MessageCircle,
  Search,
  Star,
} from 'lucide-react';
import { CTA_VARIANT } from '../../config/featureFlags';
import { getCtaVariantColor, trackCtaClick } from '../../utils/ctaVariant';
import { db } from '../../firebaseConfig';
import { collection, collectionGroup, getDocs, limit, query, where } from 'firebase/firestore';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Chip } from '../ui/Chip';
import { Input } from '../ui/Input';

/**
 * Firestore indexes recomendados para HomePage.jsx
 *
 * INDEX 1
 * collectionGroup: produtos
 * field: status
 * order: ASC
 *
 * INDEX 2 (se houver ordenação por data)
 * collectionGroup: produtos
 * field: status ASC
 * field: createdAt DESC
 *
 * INDEX 3 (se houver ordenação por popularidade)
 * collectionGroup: produtos
 * field: status ASC
 * field: pedidos DESC
 */

const copyOptions = [
  {
    title: 'Peça em poucos cliques, entrega rápida no seu bairro.',
    subtitle: 'Pagamento seguro, suporte no WhatsApp e avaliações reais de clientes próximos.',
    description:
      'Escolha a categoria, compare tempo e frete em segundos e finalize com confiança. Nossa vitrine reúne restaurantes e lojas locais com ofertas atualizadas para acelerar sua decisão.',
  },
  {
    title: 'Seu pedido favorito, do jeito mais simples.',
    subtitle: 'Acompanhe o preparo em tempo real e conte com atendimento humano quando precisar.',
    description:
      'Busque por prato, descubra promoções e encontre os mais bem avaliados sem rolar infinitamente. A experiência foi desenhada para reduzir o tempo até o primeiro clique.',
  },
  {
    title: 'Comida local com preço justo e entrega confiável.',
    subtitle: 'Parceiros da região, taxas transparentes e ofertas relevantes para o seu endereço.',
    description:
      'Valorizamos restaurantes locais e mostramos primeiro o que realmente entrega qualidade perto de você. Assim você escolhe melhor, economiza tempo e recebe mais rápido.',
  },
];

const quickCategories = ['Pizza', 'Hambúrguer', 'Japonês', 'Mercado', 'Sobremesa', 'Bebidas'];

const EMPTY_STATE_LABEL = 'Nenhum produto ativo disponível no momento';
const DEFAULT_REGION_LABEL = 'Goiânia e região';
const PRODUCT_PLACEHOLDER_IMAGE = '/images/product-placeholder.png';
const ACTIVE_STATUS_VALUES = [
  'Ativo',
  'ativo',
  'ACTIVE',
  'active',
  'Publicado',
  'publicado',
  'Disponível',
  'Disponivel',
  'disponível',
  'disponivel',
];
const ACTIVE_BOOLEAN_FIELDS = ['active', 'isActive', 'ativo', 'published', 'disponivel', 'visivel'];

const buildCatalogProduct = (docSnap, product = {}, storeInfoById = new Map()) => {
  const storeId = product.lojaId || docSnap.ref.parent.parent?.id || '';
  const storeInfo = storeInfoById.get(storeId) || {};

  return {
    id: storeId ? `${storeId}:${docSnap.id}` : docSnap.id,
    productId: docSnap.id,
    nome: product.nome || product.name || 'Produto',
    preco: product.preco,
    lojaId: storeId,
    lojaNome: storeInfo.nome || storeInfo.name || storeInfo.displayName || product.lojaNome || 'Loja',
    status: product.status,
    active: product.active,
    isActive: product.isActive,
    ativo: product.ativo,
    published: product.published,
    disponivel: product.disponivel,
    visivel: product.visivel,
    tempoEntrega: product.tempoEntrega,
    tempoEstimado: product.tempoEstimado,
    tempoPreparo: product.tempoPreparo,
    taxaEntrega: product.taxaEntrega,
    frete: product.frete,
    updatedAt: product.updatedAt,
    createdAt: product.createdAt,
    totalPedidos: product.totalPedidos,
    maisPedido: product.maisPedido,
    orderCount: product.orderCount,
    pedidosCount: product.pedidosCount,
    salesCount: product.salesCount,
    rating: product.rating,
    avaliacao: product.avaliacao,
    nota: product.nota,
    promocao: product.promocao,
    promo: product.promo,
    isPromotion: product.isPromotion,
    freteGratis: product.freteGratis,
    cupom: product.cupom,
    cupomCodigo: product.cupomCodigo,
    descontoPercentual: product.descontoPercentual,
    desconto: product.desconto,
    publicPath: product.publicPath || storeInfo.publicPath,
    cardapioPath: product.cardapioPath,
    imageUrl:
      product.imageUrl ||
      product.image ||
      product.foto ||
      product.photoUrl ||
      product.thumbnail ||
      PRODUCT_PLACEHOLDER_IMAGE,
  };
};

const isPermissionError = (err) => {
  const errorMessage = String(err?.message || '').toLowerCase();
  return (
    err?.code === 'permission-denied' ||
    errorMessage.includes('missing or insufficient permissions')
  );
};

const isInstallationsError = (err) => {
  const errorMessage = String(err?.message || '').toLowerCase();
  return (
    err?.code === 'installations/request-failed' ||
    errorMessage.includes('installations') ||
    errorMessage.includes('permission_denied: the caller does not have permission')
  );
};

const isMissingIndexError = (err) => {
  const errorMessage = String(err?.message || '').toLowerCase();
  return (
    err?.code === 'failed-precondition' ||
    errorMessage.includes('requires an index') ||
    errorMessage.includes('collection_group_asc index')
  );
};

const logHomeCatalogFallbackReason = (error, contextLabel) => {
  if (isMissingIndexError(error) || isPermissionError(error)) {
    console.warn(`[Home] ${contextLabel} indisponível, tentando próxima estratégia.`);
    return;
  }

  if (isInstallationsError(error)) {
    console.warn('[Home] Erro de Firebase Installations detectado; vitrine seguirá sem bloqueio.');
    return;
  }

  console.warn(`[Home] Falha na consulta ${contextLabel}:`, error);
};

const mergeAndNormalizeProducts = (products = []) => {
  const uniqueProducts = new Map();

  products.forEach((product) => {
    if (!product?.id || !isProductActive(product)) return;
    uniqueProducts.set(product.id, product);
  });

  return Array.from(uniqueProducts.values())
    .sort((a, b) => resolveDateValue(b.updatedAt) - resolveDateValue(a.updatedAt))
    .slice(0, 50);
};

const fetchStoreInfoMap = async () => {
  try {
    const storesSnapshot = await getDocs(query(collection(db, 'lojas'), limit(100)));
    return new Map(
      storesSnapshot.docs.map((storeDoc) => [
        storeDoc.id,
        {
          id: storeDoc.id,
          ...(storeDoc.data() || {}),
        },
      ])
    );
  } catch (error) {
    logHomeCatalogFallbackReason(error, 'lojas');
    return new Map();
  }
};

const buildActiveProductQueryPlans = (sourceLabel, sourceRefFactory) => [
  ...ACTIVE_STATUS_VALUES.map((statusValue) => ({
    sourceLabel: `${sourceLabel}/status=${statusValue}`,
    sourceRefFactory,
    field: 'status',
    value: statusValue,
  })),
  ...ACTIVE_BOOLEAN_FIELDS.map((fieldName) => ({
    sourceLabel: `${sourceLabel}/${fieldName}=true`,
    sourceRefFactory,
    field: fieldName,
    value: true,
  })),
];

const fetchProductsByField = async ({ sourceLabel, sourceRefFactory, field, value, storeInfoById }) => {
  try {
    const sourceRef = sourceRefFactory();
    const snapshot = await getDocs(query(sourceRef, where(field, '==', value), limit(50)));

    return snapshot.docs.map((docSnap) =>
      buildCatalogProduct(docSnap, docSnap.data() || {}, storeInfoById)
    );
  } catch (error) {
    logHomeCatalogFallbackReason(error, sourceLabel);
    return [];
  }
};

const fetchActiveProductsCatalog = async () => {
  const storeInfoById = await fetchStoreInfoMap();

  const fetchProductsFromStoreSubcollections = async () => {
    try {
      const storeIds = Array.from(storeInfoById.keys()).slice(0, 25);
      if (!storeIds.length) return [];

      const perStoreSnapshots = await Promise.allSettled(
        storeIds.flatMap((storeId) =>
          buildActiveProductQueryPlans(
            `lojas/${storeId}/produtos`,
            () => collection(db, 'lojas', storeId, 'produtos')
          ).map((plan) =>
            fetchProductsByField({
              ...plan,
              storeInfoById,
            })
          )
        )
      );

      const products = perStoreSnapshots.flatMap((result) => {
        if (result.status !== 'fulfilled') return [];
        return result.value;
      });

      return mergeAndNormalizeProducts(products);
    } catch (error) {
      logHomeCatalogFallbackReason(error, 'lojas/*/produtos');
      return [];
    }
  };

  const queryPlans = [
    ...buildActiveProductQueryPlans(
      'collectionGroup(produtos)',
      () => collectionGroup(db, 'produtos')
    ),
    ...buildActiveProductQueryPlans(
      'root(produtos)',
      () => collection(db, 'produtos')
    ),
  ].map((plan) => ({
    ...plan,
    storeInfoById,
  }));

  const settledResults = await Promise.allSettled(
    queryPlans.map((plan) => fetchProductsByField(plan))
  );

  const collectedProducts = settledResults.flatMap((result) => {
    if (result.status === 'fulfilled') return result.value;
    console.warn('[Home] Estratégia de carregamento não concluída:', result.reason);
    return [];
  });

  const normalizedByQuery = mergeAndNormalizeProducts(collectedProducts);
  if (normalizedByQuery.length > 0) return normalizedByQuery;

  return fetchProductsFromStoreSubcollections();
};

const resolveDateValue = (value) => {
  if (!value) return 0;
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
};

const resolveNumber = (value) => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const normalized = Number(value.replace(',', '.'));
    return Number.isFinite(normalized) ? normalized : 0;
  }
  return 0;
};

const isProductActive = (product = {}) => {
  if (typeof product.status === 'string' && ACTIVE_STATUS_VALUES.includes(product.status.trim())) {
    return true;
  }

  return ACTIVE_BOOLEAN_FIELDS.some((fieldName) => product[fieldName] === true);
};

const resolvePopularity = (item = {}) => {
  const popularityKeys = ['totalPedidos', 'maisPedido', 'orderCount', 'pedidosCount', 'salesCount'];
  return popularityKeys.reduce((acc, key) => {
    const value = resolveNumber(item[key]);
    return value > acc ? value : acc;
  }, 0);
};

const resolveRating = (item = {}) => {
  return resolveNumber(item.rating ?? item.avaliacao ?? item.nota);
};

const isPromotionalProduct = (item = {}) => {
  return Boolean(
    item.promocao ||
      item.promo ||
      item.isPromotion ||
      item.freteGratis ||
      item.cupom ||
      item.cupomCodigo ||
      resolveNumber(item.descontoPercentual) > 0 ||
      resolveNumber(item.desconto) > 0
  );
};

const getBadgeForSection = (sectionName, item) => {
  if (sectionName === 'Populares perto de você' && resolvePopularity(item) > 0) return 'Mais pedido';
  if (sectionName === 'Promoções / Frete grátis / Cupons') {
    if (item.cupomCodigo) return `Cupom ${item.cupomCodigo}`;
    if (item.freteGratis) return 'Frete grátis';
    if (isPromotionalProduct(item)) return 'Promoção';
  }
  if (sectionName === 'Bem avaliados' && resolveRating(item) > 0) return 'Bem avaliado';
  if (sectionName === 'Novos') return 'Novo';
  return '';
};

const formatCurrency = (value) => {
  const amount = resolveNumber(value);
  return amount <= 0 ? '—' : `R$ ${amount.toFixed(2).replace('.', ',')}`;
};

const sectionDefinitions = [
  {
    name: 'Populares perto de você',
    sortFn: (a, b) => {
      const popularityDiff = resolvePopularity(b) - resolvePopularity(a);
      if (popularityDiff !== 0) return popularityDiff;
      return resolveDateValue(b.updatedAt) - resolveDateValue(a.updatedAt);
    },
  },
  {
    name: 'Promoções / Frete grátis / Cupons',
    filterFn: (item) => isPromotionalProduct(item),
    sortFn: (a, b) => resolveDateValue(b.updatedAt) - resolveDateValue(a.updatedAt),
  },
  {
    name: 'Bem avaliados',
    sortFn: (a, b) => {
      const ratingDiff = resolveRating(b) - resolveRating(a);
      if (ratingDiff !== 0) return ratingDiff;
      return resolveDateValue(b.updatedAt) - resolveDateValue(a.updatedAt);
    },
  },
  {
    name: 'Novos',
    sortFn: (a, b) => resolveDateValue(b.createdAt) - resolveDateValue(a.createdAt),
  },
];

export const HomePage = () => {
  const ctaVariantColor = getCtaVariantColor();
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [allActiveProducts, setAllActiveProducts] = useState([]);
  const [regionLabel, setRegionLabel] = useState(DEFAULT_REGION_LABEL);
  const [geoFailed, setGeoFailed] = useState(false);

  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setGeoFailed(true);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      () => {
        setGeoFailed(false);
      },
      () => {
        setGeoFailed(true);
        setRegionLabel(DEFAULT_REGION_LABEL);
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 120000 }
    );
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadProducts = async () => {
      setLoadingProducts(true);

      try {
        const activeProducts = await fetchActiveProductsCatalog();

        if (!isMounted) return;

        setAllActiveProducts(activeProducts);
      } catch (error) {
        console.error('Erro ao carregar produtos ativos da Home:', error);
        if (isMounted) setAllActiveProducts([]);
      } finally {
        if (isMounted) setLoadingProducts(false);
      }
    };

    loadProducts();
    return () => {
      isMounted = false;
    };
  }, []);

  const sectionCards = useMemo(() => {
    return sectionDefinitions.map((section) => {
      const filtered = section.filterFn
        ? allActiveProducts.filter(section.filterFn)
        : [...allActiveProducts];

      const cards = filtered
        .sort(section.sortFn)
        .slice(0, 3)
        .map((item) => ({
          id: item.id,
          name: item.nome || item.name || 'Produto',
          rating: resolveRating(item),
          eta: item.tempoEntrega || item.tempoEstimado || item.tempoPreparo || '—',
          fee: formatCurrency(item.taxaEntrega ?? item.frete),
          tag: getBadgeForSection(section.name, item),
          price: formatCurrency(item.preco),
          storeName: item.lojaNome || 'Loja',
          href: item.publicPath || item.cardapioPath || null,
          imageUrl: item.imageUrl || item.image || item.foto || PRODUCT_PLACEHOLDER_IMAGE,
        }));

      return {
        name:
          section.name === 'Populares perto de você'
            ? `Populares em ${regionLabel}`
            : section.name,
        cards,
      };
    });
  }, [allActiveProducts, regionLabel]);

  return (
    <main className="home-page">
      <section className="home-search-section container-shell" aria-label="Busca de restaurantes e pratos">
        <div className="home-search-wrap">
          <Search className="search-icon" size={18} aria-hidden="true" />
          <Input aria-label="Buscar pratos" placeholder="Buscar pratos, restaurantes, doces, …" />
        </div>
      </section>

      <section className="hero container-shell">
        {geoFailed ? (
          <p className="text-xs text-gray-500 mb-3">
            Localização indisponível. Exibindo catálogo geral de {regionLabel}.
          </p>
        ) : null}
        <p className="eyebrow">Entrega local com curadoria</p>
        <h1>{copyOptions[0].title}</h1>
        <p className="hero-subtitle">{copyOptions[0].subtitle}</p>
        <p className="hero-description">{copyOptions[0].description}</p>
        <div className="hero-cta-row">
          <Button
            variant={ctaVariantColor}
            onClick={trackCtaClick}
            aria-label={`Pedir agora - variante ${CTA_VARIANT}`}
          >
            Pedir agora
          </Button>
          <Button variant="ghost">Explorar categorias</Button>
        </div>
        <div className="chip-row" role="list" aria-label="Categorias rápidas">
          {quickCategories.map((category) => (
            <Chip key={category} role="listitem" aria-label={`Categoria ${category}`}>
              {category}
            </Chip>
          ))}
        </div>
      </section>

      {sectionCards.map(({ name: sectionName, cards }) => (
        <section key={sectionName} className="container-shell section-spacing">
          <div className="section-title-row">
            <h2>{sectionName}</h2>
          </div>
          <div className="cards-grid">
            {!loadingProducts && cards.length === 0 && (
              <Card>
                <div className="card-meta">
                  <span>
                    {geoFailed
                      ? `Ainda não há itens para ${regionLabel}. Mostraremos novos produtos assim que forem publicados.`
                      : EMPTY_STATE_LABEL}
                  </span>
                </div>
              </Card>
            )}

            {cards.map((item) => (
              <Card key={`${sectionName}-${item.id || item.name}`}>
                <button
                  type="button"
                  onClick={() => item.href && window.open(item.href, '_self')}
                  className="w-full text-left"
                >
                  <div className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition">
                    <img
                      src={item.imageUrl || PRODUCT_PLACEHOLDER_IMAGE}
                      alt={item.name}
                      className="w-16 h-16 md:w-24 md:h-24 object-cover rounded-lg"
                      loading="lazy"
                    />

                    <div className="flex flex-col min-w-0 flex-1">
                      <div className="card-top">
                        <h3 className="truncate">{item.name}</h3>
                        {item.tag ? <Badge>{item.tag}</Badge> : null}
                      </div>
                      <p className="text-sm text-gray-500 mb-1 truncate">{item.storeName}</p>
                      <p className="text-sm font-medium text-gray-700 mb-2">{item.price}</p>
                      <div className="card-meta">
                        {item.rating > 0 ? (
                          <span>
                            <Star size={14} aria-hidden="true" /> {item.rating.toFixed(1)}
                          </span>
                        ) : null}
                        <span>
                          <Clock3 size={14} aria-hidden="true" /> {item.eta}
                        </span>
                        <span>{item.fee}</span>
                      </div>
                    </div>
                  </div>
                </button>
              </Card>
            ))}
          </div>
        </section>
      ))}

      <section className="container-shell section-spacing trust-block">
        <h2>Confiança para pedir sem dúvidas</h2>
        <div className="trust-grid">
          <p>
            <CreditCard size={16} aria-hidden="true" /> Cartão, Pix e carteira digital com confirmação rápida.
          </p>
          <p>
            <MessageCircle size={16} aria-hidden="true" /> Suporte via WhatsApp e chat durante todo o pedido.
          </p>
          <p>
            Links úteis: <a href="#">Como funciona</a> · <a href="#">Trocas/Cancelamento</a> ·{' '}
            <a href="#">Termos/Privacidade</a>
          </p>
        </div>
      </section>

      <footer className="home-footer">
        <div className="container-shell footer-grid">
          <div>
            <strong>APFood Goiânia</strong>
            <p>Av. Comercial, 433 · Jardim Nova Esperança</p>
            <p>Seg-Sex 09:30-18:30 · Sáb 09:00-14:00</p>
          </div>
          <div>
            <strong>Redes e contato</strong>
            <p>Instagram · WhatsApp · Chat online</p>
          </div>
          <div>
            <strong>Copy pronta (3 opções)</strong>
            <ul className="copy-list">
              {copyOptions.map((copy) => (
                <li key={copy.title}>
                  <span>{copy.title}</span>
                  <small>{copy.subtitle}</small>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </footer>
    </main>
  );
};
